import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  hasAnyPermission as profileHasAnyPermission,
  hasAllPermissions as profileHasAllPermissions,
  hasPermission as profileHasPermission,
  hasRole as profileHasRole,
  isSuperAdmin as profileIsSuperAdmin,
} from '@/shared/helpers/user'
import type { UserProfile, UserProfileInput } from '@/shared/types/user.type'

function normalizeProfile(profile: UserProfileInput): UserProfile {
  return {
    ...profile,
    roles: profile.roles ?? [],
    permissions: profile.permissions ?? [],
  }
}

export const useUserStore = defineStore('user', () => {
  const profile = ref<UserProfile | null>(null)

  const roles = computed(() => profile.value?.roles ?? [])
  const permissions = computed(() => profile.value?.permissions ?? [])
  const isSuperAdmin = computed(() => profileIsSuperAdmin(profile.value))

  function setProfile(nextProfile: UserProfileInput | null) {
    profile.value = nextProfile ? normalizeProfile(nextProfile) : null
  }

  function clearProfile() {
    profile.value = null
  }

  function hasRole(roleCode: string) {
    return profileHasRole(profile.value, roleCode)
  }

  function hasPermission(permission: string) {
    return profileHasPermission(profile.value, permission)
  }

  function hasAnyPermission(nextPermissions: string[]) {
    return profileHasAnyPermission(profile.value, nextPermissions)
  }

  function hasAllPermissions(nextPermissions: string[]) {
    return profileHasAllPermissions(profile.value, nextPermissions)
  }

  return {
    profile,
    roles,
    permissions,
    isSuperAdmin,
    setProfile,
    clearProfile,
    hasRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  }
})
